import React, { useState, Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { selectSelectedServiceLocation } from 'containers/PrimoAccount/selectors';
import { Drawer, Button, Div, H1, P } from 'components/Ui';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { titleCase } from 'utils/string';
import { showDrawer } from 'containers/DrawerManager/actions';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import { fetchDeliveryNotes, fetchCustomerNotes } from '../actions';
import DeliveryInformationForm from './form';
import GenericBlock from '../GenericBlock';
import commonStyles from '../styles.scss';
import styles from './styles.scss';

import { EDIT_CUSTOMER_NOTES } from '../BillingInformation/constants';
import { selectCustomerNotes } from '../selectors';

function Delivery({
  serviceLocation,
  getDeliveryNotes,
  getCustomerNotes,
  customerNotes,
}) {
  const [showForm, changeShowForm] = useState(false);
  const dispatch = useDispatch();

  const selectedDeliveryLocation =
    customerNotes && serviceLocation
      ? customerNotes.serviceLocations.find((a) => a.id === serviceLocation.id)
      : null;

  useEffect(() => {
    getCustomerNotes();
    getDeliveryNotes();
  }, []);

  const handleHideForm = () => {
    changeShowForm(false);
  };

  const {
    address1: streetAddress,
    city,
    stateOrProvinceCode: state,
    postalCode: zip,
  } = serviceLocation || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleShowCustomerNotes = useCallback(() => {
    dispatch(showDrawer(EDIT_CUSTOMER_NOTES));
  });

  const whatToAdd = selectedDeliveryLocation
    ? 'Additional Instructions'
    : 'Delivery Instructions';
  const whatToAddInstructions =
    selectedDeliveryLocation && selectedDeliveryLocation.additionalInstructions
      ? selectedDeliveryLocation.additionalInstructions
      : `To add ${whatToAdd.toLowerCase()}, click the link below`;

  return (
    <GenericBlock title="Delivery information">
      <Fragment>
        <div
          className={commonStyles.container}
          data-tracking={GTM_TRACKING_TAGS['Delivery information']}
        >
          <div className={commonStyles.blockContainer}>
            {!isMobile && (
              <div className={commonStyles.blockHeaderContainer}>
                <H1 className={commonStyles.blockHeaderText}>
                  Delivery information
                </H1>
              </div>
            )}
            <div className={commonStyles.blockContent}>
              <Fragment>
                <div className={commonStyles.section}>
                  <div className={commonStyles.subBlock}>
                    <Div className={commonStyles.subTitle}>
                      Delivery Address
                    </Div>
                    {serviceLocation && (
                      <div>
                        <div>{titleCase(streetAddress)}</div>
                        <div>
                          {titleCase(city)}, {state} {zip}
                        </div>
                      </div>
                    )}
                    <Div className={styles.subText}>
                      Need to edit? Call 866-407-7873
                    </Div>
                  </div>
                </div>
                <div className={commonStyles.section}>
                  <div className={commonStyles.subBlock}>
                    <Div className={commonStyles.subTitle}>Location</Div>
                    <p style={{ textTransform: 'capitalize' }}>
                      {selectedDeliveryLocation &&
                      selectedDeliveryLocation.note &&
                      selectedDeliveryLocation.noteType
                        ? `${selectedDeliveryLocation.noteType}, ${selectedDeliveryLocation.note}`.toLowerCase()
                        : '-'}
                    </p>
                    <Button
                      text="Edit Location"
                      onClick={handleShowCustomerNotes}
                      link
                      bold
                    />
                  </div>
                </div>
                <div className={commonStyles.section}>
                  <div className={commonStyles.subBlock}>
                    <Div className={commonStyles.subTitle}>{whatToAdd}</Div>
                    <P>{whatToAddInstructions}</P>
                    <Button
                      text="Edit Instructions"
                      onClick={handleShowCustomerNotes}
                      link
                      bold
                    />
                  </div>
                </div>
              </Fragment>
            </div>
          </div>
        </div>
        <Drawer
          headerTitle="Edit address"
          open={showForm}
          onClose={handleHideForm}
        >
          <DeliveryInformationForm
            onClose={handleHideForm}
            values={{
              streetAddress,
              city,
              state,
              zip,
            }}
          />
        </Drawer>
      </Fragment>
    </GenericBlock>
  );
}

Delivery.propTypes = {
  serviceLocation: PropTypes.shape({
    id: PropTypes.string,
    address1: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    stateOrProvinceCode: PropTypes.string,
    deliveryNotes: PropTypes.object,
  }),
};

const mapStateToProps = createStructuredSelector({
  serviceLocation: selectSelectedServiceLocation(),
  customerNotes: selectCustomerNotes(),
});

const mapDispatchToProps = {
  getDeliveryNotes: fetchDeliveryNotes,
  getCustomerNotes: fetchCustomerNotes,
};

export default connect(mapStateToProps, mapDispatchToProps)(Delivery);
